// Defensive localStorage helpers.
//
// SECURITY CONTRACT (read carefully before adding new keys):
//   This module is ONLY for non-sensitive UI state, the current step in a workshop, the user's
//   own concept text, selected card IDs, mode toggles, custom-database notes the user explicitly
//   pasted, etc. We do NOT store: API keys, auth tokens, session JWTs, third-party PII, or any
//   secret material. Sensitive credentials live server-side only.
//
//   A runtime guard rejects keys whose names look sensitive so future contributors can't
//   accidentally widen the contract. If you genuinely need to add a sensitive key, change the
//   storage strategy (httpOnly cookie / encrypted store), do NOT relax this guard.
//
// All operations are wrapped in try/catch because some browsers / private modes disable
// localStorage entirely (which throws). We degrade gracefully to in-memory.

import { logger } from "@/lib/logger";

const SENSITIVE_HINTS = [
  "token",
  "jwt",
  "auth",
  "password",
  "passwd",
  "secret",
  "apikey",
  "api-key",
  "api_key",
  "private",
  "credit",
  "ssn",
];

function _assertNonSensitive(key) {
  const k = String(key || "").toLowerCase();
  for (const hint of SENSITIVE_HINTS) {
    if (k.includes(hint)) {
      // Loud development-time warning; in production the logger is a no-op so we still don't store it.
      logger.error(
        `storage: refusing to write key matching sensitive hint "${hint}". Use a server-side store or httpOnly cookie instead.`
      );
      return false;
    }
  }
  return true;
}

export function lsGet(key, fallback) {
  try {
    if (typeof window === "undefined") return fallback;
    // SAFE: `_assertNonSensitive` (the gatekeeper for lsSet) refuses to write
    // any key matching a sensitive hint (token, jwt, auth, password, ssn, …),
    // so anything read back from this store is project state, not PII.
    // eslint-disable-next-line no-restricted-globals
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    try {
      return JSON.parse(raw);
    } catch (parseErr) {
      logger.warn("lsGet: non-JSON value at key, returning raw string", { key, error: parseErr?.message });
      return raw;
    }
  } catch (err) {
    logger.warn("lsGet: localStorage unavailable, using fallback", { key, error: err?.message });
    return fallback;
  }
}

export function lsSet(key, value) {
  // SECURITY: refuse to persist any key that looks like sensitive PII / a
  // credential. The hint list lives in `SENSITIVE_HINTS` above. If you need to
  // persist sensitive data, do it server-side or in an httpOnly cookie, never
  // in localStorage.
  if (!_assertNonSensitive(key)) return;
  try {
    if (typeof window === "undefined") return;
    const serialized = typeof value === "string" ? value : JSON.stringify(value);
    // eslint-disable-next-line no-restricted-globals
    window.localStorage.setItem(key, serialized);
  } catch (err) {
    logger.warn("lsSet: localStorage write failed", { key, error: err?.message });
  }
}

export function lsRemove(key) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(key);
  } catch (err) {
    logger.warn("lsRemove: localStorage remove failed", { key, error: err?.message });
  }
}

export function lsClearPrefix(prefix) {
  try {
    if (typeof window === "undefined") return;
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(prefix)) toRemove.push(k);
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch (err) {
    logger.warn("lsClearPrefix: localStorage clear failed", { prefix, error: err?.message });
  }
}
